import { StackDeploymentInfo } from '@/react/common/stacks/types';

import { confirm } from '@@/modals/confirm';

import { RepoConfigResponse } from './types';

export function confirmEnableTLSVerify() {
  return confirm({
    title: 'Enable TLS Verification?',
    message:
      'Enabling the verification of TLS certificates without ensuring the correct configuration of your Certificate Authority (CA) for self-signed certificates can result in deployment failures.',
  });
}

export function getRepositoryPathWithoutHost(input: string) {
  if (!input) {
    return '';
  }

  const trimmed = input.trim();

  // Handle scp-like SSH syntax: git@host:owner/repo(.git)
  const scpMatch = trimmed.match(/^[^@\s]+@[^:]+:(.+)$/);
  if (scpMatch && scpMatch[1]) {
    return sanitizePath(scpMatch[1]);
  }

  // Try URL parsing (https://, ssh://, git+ssh://, etc.)
  try {
    const url = new URL(trimmed);
    const path = url.pathname || '';
    return sanitizePath(path.replace(/^\/+/, ''));
  } catch {
    // Fallback best-effort: strip protocol and hostname if present
    const withoutProtocol = trimmed.replace(
      /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//,
      ''
    );
    const firstSlash = withoutProtocol.indexOf('/');
    if (firstSlash >= 0) {
      return sanitizePath(withoutProtocol.slice(firstSlash + 1));
    }
    return sanitizePath(withoutProtocol);
  }

  function sanitizePath(p: string) {
    return p
      .replace(/^\/+/, '')
      .replace(/\.git$/, '')
      .replace(/\/$/, '');
  }
}

export interface GitConfigDivergence {
  url?: string;
  reference?: string;
  configFilePath?: string;
}

export function getGitConfigDivergence(
  gitConfig: RepoConfigResponse,
  currentDeploymentInfo: StackDeploymentInfo | null | undefined
): GitConfigDivergence | null {
  if (!currentDeploymentInfo) return null;

  // treat a missing currentDeploymentInfo field as unchanged
  const urlChanged =
    typeof currentDeploymentInfo.RepositoryURL !== 'undefined' &&
    currentDeploymentInfo.RepositoryURL !== gitConfig.URL;

  const refChanged =
    typeof currentDeploymentInfo.ReferenceName !== 'undefined' &&
    currentDeploymentInfo.ReferenceName !== gitConfig.ReferenceName;

  const fileChanged =
    typeof currentDeploymentInfo.ConfigFilePath !== 'undefined' &&
    currentDeploymentInfo.ConfigFilePath !== gitConfig.ConfigFilePath;

  if (!urlChanged && !refChanged && !fileChanged) return null;

  return {
    url: urlChanged ? gitConfig.URL : undefined,
    reference: refChanged ? gitConfig.ReferenceName : undefined,
    // always shown once diverged, regardless of which field changed
    configFilePath: gitConfig.ConfigFilePath,
  };
}

export function isGitConfigDiverged(
  gitConfig: RepoConfigResponse,
  currentDeploymentInfo: StackDeploymentInfo | null | undefined
) {
  return getGitConfigDivergence(gitConfig, currentDeploymentInfo) !== null;
}
